import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { API_BASE_URL } from '../http/api-base-url.token';
import { AuthService } from './auth.service';
import { CandidateAuthService } from './candidate-auth.service';

function requestPath(requestUrl: string): string {
  return new URL(requestUrl, location.origin).pathname;
}

function isApiRequest(requestUrl: string, apiBaseUrl: string): boolean {
  const requestOrigin = new URL(requestUrl, location.origin).origin;
  const apiOrigin = new URL(apiBaseUrl, location.origin).origin;
  return requestOrigin === apiOrigin;
}

/**
 * Candidate scheme detection: the candidate surface owns every path under `${apiBase}/candidate/`
 * (candidate/register, candidate/login, candidate/refresh, candidate/logout, candidate/me/*,
 * candidate/applications/*). Everything else on the API origin is the company scheme.
 */
function isCandidateApiRequest(requestUrl: string, apiBaseUrl: string): boolean {
  const apiPath = new URL(apiBaseUrl, location.origin).pathname.replace(/\/+$/, '');
  return requestPath(requestUrl).startsWith(`${apiPath}/candidate/`);
}

function isUnauthenticatedAuthRequest(
  requestUrl: string,
  apiBaseUrl: string,
  unauthenticatedPaths: readonly string[],
): boolean {
  const apiPath = new URL(apiBaseUrl, location.origin).pathname.replace(/\/+$/, '');
  const path = requestPath(requestUrl);
  return unauthenticatedPaths.some(unauthenticated => path === `${apiPath}/${unauthenticated}`);
}

/**
 * Bearer injection plus the 401 flow for BOTH rekruto auth schemes: refresh once (the two
 * engines dedupe their own in-flight refresh), retry the request once with the new pair, and
 * sign out when either step fails.
 *
 * A 401 must reach the caller untouched when it is an expected outcome of the call itself -
 * network errors (status 0), CORS failures, 404s, 500s, anything from a non-API origin, and a
 * 401 from a credential endpoint rejecting that call (bad credentials, unknown or expired
 * token) never sign the user out.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const apiBaseUrl = inject(API_BASE_URL);
  const auth = isCandidateApiRequest(req.url, apiBaseUrl) ? inject(CandidateAuthService) : inject(AuthService);
  const targetsApi = isApiRequest(req.url, apiBaseUrl);

  const token = auth.token();
  const authReq = token && targetsApi ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req;

  return next(authReq).pipe(
    catchError((error: unknown) => {
      if (
        !targetsApi ||
        !(error instanceof HttpErrorResponse) ||
        error.status !== 401 ||
        isUnauthenticatedAuthRequest(req.url, apiBaseUrl, auth.unauthenticatedPaths)
      ) {
        return throwError(() => error);
      }

      return auth.refresh().pipe(
        switchMap(() => {
          const retryToken = auth.token();
          const retryReq =
            retryToken && targetsApi ? req.clone({ setHeaders: { Authorization: `Bearer ${retryToken}` } }) : req;
          return next(retryReq);
        }),
        // The refresh failed, or the retried request was rejected again: sign out and surface
        // the ORIGINAL error - the caller asked for data, not for a refresh diagnosis.
        catchError(() => {
          auth.logout();
          return throwError(() => error);
        }),
      );
    }),
  );
};